// TaskFilters — Drawer с фильтрами по status / priority / assignee + кнопка "Сбросить".
// Используется в TasksListPage. Полностью controlled (value/onChange): локальный draft
// удалён (§6.2 №1) — update() звал onChange сразу, поэтому draft всегда дублировал
// parent-value; канон — единственный источник истины в родителе.
import {
  Drawer,
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  type SelectChangeEvent,
} from '@mui/material';
import type { TaskStatus, TaskPriority } from '@/api/tasks';

export interface TaskFiltersValue {
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeUserId?: string;
}

interface TaskFiltersProps {
  /** Текущие значения фильтров. */
  value: TaskFiltersValue;
  /** Колбэк при изменении фильтров. */
  onChange: (next: TaskFiltersValue) => void;
  /** Флаг открытия Drawer. */
  open?: boolean;
  /** Колбэк закрытия Drawer. */
  onClose?: () => void;
}

const STATUS_OPTIONS: TaskStatus[] = ['NEW', 'IN_PROGRESS', 'BLOCKED', 'DONE', 'CANCELLED'];
const PRIORITY_OPTIONS: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

/** Drawer с фильтрами задач (status / priority / assignee). */
export function TaskFilters({ value, onChange, open = false, onClose }: TaskFiltersProps): JSX.Element {
  function update<K extends keyof TaskFiltersValue>(key: K, next: TaskFiltersValue[K]): void {
    onChange({ ...value, [key]: next });
  }

  function handleReset(): void {
    onChange({});
  }

  const content = (
    <Box sx={{ width: 320, p: 3 }} data-testid="task-filters">
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
        Фильтры
      </Typography>
      <Stack spacing={2} divider={<Divider flexItem />}>
        <FormControl fullWidth size="small">
          <InputLabel id="task-filter-status-label">Статус</InputLabel>
          <Select
            labelId="task-filter-status-label"
            label="Статус"
            value={value.status ?? ''}
            onChange={(e: SelectChangeEvent<string>) => {
              const v = e.target.value as string;
              update('status', (v === '' ? undefined : v) as TaskStatus);
            }}
          >
            <MenuItem value="">Любой</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel id="task-filter-priority-label">Приоритет</InputLabel>
          <Select
            labelId="task-filter-priority-label"
            label="Приоритет"
            value={value.priority ?? ''}
            onChange={(e: SelectChangeEvent<string>) => {
              const v = e.target.value as string;
              update('priority', (v === '' ? undefined : v) as TaskPriority);
            }}
          >
            <MenuItem value="">Любой</MenuItem>
            {PRIORITY_OPTIONS.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel id="task-filter-assignee-label">ID исполнителя</InputLabel>
          <Select
            labelId="task-filter-assignee-label"
            label="ID исполнителя"
            value={value.assigneeUserId ?? ''}
            onChange={(e: SelectChangeEvent<string>) => {
              const v = e.target.value as string;
              update('assigneeUserId', v === '' ? undefined : v);
            }}
          >
            <MenuItem value="">Любой</MenuItem>
            <MenuItem value="me">Я</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
        <Button variant="outlined" onClick={handleReset} fullWidth>
          Сбросить
        </Button>
        {onClose ? (
          <Button variant="contained" onClick={onClose} fullWidth>
            Применить
          </Button>
        ) : null}
      </Box>
    </Box>
  );

  if (open !== undefined && onClose) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        {content}
      </Drawer>
    );
  }
  return content;
}
